import { Injectable, NgZone } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Subject,
  switchMap,
} from 'rxjs';
import { TrackType } from 'src/app/common/interfaces/rtc-interface';
import { DeviceType } from 'src/app/common/utils/devices-handle-util';
import { DeviceErrorType, DeviceState, DeviceTogglingStatus } from 'src/app/models/device-manger';
import {
  deviceHasPermissionError,
  DevicesManagerService,
} from 'src/app/services/devices-manager.service';
import { LocalTracksManagerService } from 'src/app/services/local-tracks-manager.service';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { SpacePermissionsManagerService } from 'src/app/services/space-permissions-manager.service';
import { VideoCallTracksStateService } from 'src/app/services/video-call-tracks-state.service';
import { TruncateStringPipe } from 'src/app/standalones/pipes/truncate-string.pipe';
import { enterZone } from 'src/app/utilities/ZoneUtils';
import { KeyboardShortcutsCheckService } from 'src/app/services/keyboard-shortcuts-check.service';
import { VideoCallControlsService } from './video-call-controls.service';
import { PictureInPictureService } from './picture-in-picture.service';

@UntilDestroy()
@Injectable()
export class MicButtonStateService {
  private readonly _disabled$ = new BehaviorSubject<boolean>(false);
  readonly disabled$ = this._disabled$.asObservable();

  /** Pre-join unmute failed; join-call panel subscribes to open the helper guide. */
  private readonly _joinPreviewMicUnmuteFailed$ = new Subject<void>();
  readonly joinPreviewMicUnmuteFailed$ = this._joinPreviewMicUnmuteFailed$.asObservable();

  constructor(
    private localTracksManager: LocalTracksManagerService,
    private providerStateService: ProviderStateService,
    private videoCallTracksStateService: VideoCallTracksStateService,
    private translateService: TranslateService,
    private spacePermissionsManagerService: SpacePermissionsManagerService,
    private devicesManagerService: DevicesManagerService,
    private ngZone: NgZone,
    private videoCallControlsService: VideoCallControlsService,
    private pictureInPictureService: PictureInPictureService,
    private truncateStringPipe: TruncateStringPipe,
    private keyboardShortcutsCheckService: KeyboardShortcutsCheckService,
  ) {
    const disabledSource$ = combineLatest([
      this.providerStateService.callConnected$,
      this.spacePermissionsManagerService.canOpenMic$.pipe(enterZone(this.ngZone)),
      this.micLoading$,
    ]).pipe(
      map(([isCallConnected, canOpenMic, micLoading]) =>
        !isCallConnected ? micLoading : !canOpenMic || micLoading,
      ),
      distinctUntilChanged(),
    );

    disabledSource$.pipe(untilDestroyed(this)).subscribe((disabled) => {
      this._disabled$.next(disabled);
    });
  }

  isMicOpen$ = this.providerStateService.callConnected$.pipe(
    switchMap((isCallConnected) => {
      if (!isCallConnected) {
        return this.localTracksManager.micState$.pipe(
          map((s) => s.state),
          distinctUntilChanged(),
        );
      }
      return this.providerStateService.localParticipantId$.pipe(
        switchMap((localParticipantId) =>
          this.videoCallTracksStateService.getPariticpantTrackPlayingObservable(
            localParticipantId,
            TrackType.VIDEO,
            this,
            true,
          ),
        ),
      );
    }),
  );

  micLabel$ = this.isMicOpen$.pipe(
    map((micOpen) =>
      micOpen ? this.translateService.instant('Mute') : this.translateService.instant('Unmute'),
    ),
  );

  micLoading$ = this.providerStateService.callConnected$.pipe(
    switchMap((isCallConnected) => {
      if (!isCallConnected) {
        return this.localTracksManager.micTogglingStatus$.pipe(
          map((status) => status === DeviceTogglingStatus.LOADING),
          distinctUntilChanged(),
        );
      }
      return combineLatest([
        this.localTracksManager.micTogglingStatus$,
        this.providerStateService.callReconnecting$,
      ]).pipe(
        map(
          ([deviceTogglingStatus, callReconnecting]) =>
            deviceTogglingStatus === DeviceTogglingStatus.LOADING || callReconnecting,
        ),
        distinctUntilChanged(),
      );
    }),
  );

  hasError$ = combineLatest([this.localTracksManager.micState$, this.micLoading$]).pipe(
    map(([state, micLoading]) => state.hasError && !micLoading),
    distinctUntilChanged(),
  );

  micIcon$ = combineLatest([
    this.disabled$,
    this.isMicOpen$,
    this.hasError$,
    this.videoCallControlsService.showNewVctRightControls$,
    this.providerStateService.callConnected$,
  ]).pipe(
    map(([disabled, micOpen, hasError, showNewVctRightControls, isCallConnected]) => {
      if (hasError) {
        return isCallConnected ? 'mic_off_with_error_white' : 'mic_off';
      } else if (micOpen || (disabled && !showNewVctRightControls)) {
        return 'mic_icon';
      } else {
        return 'mic_off';
      }
    }),
  );

  micTooltip$ = combineLatest([
    this.localTracksManager.micState$,
    this.micLoading$,
    this.isMicOpen$,
    this.spacePermissionsManagerService.canOpenMic$.pipe(enterZone(this.ngZone)),
    this.providerStateService.callConnected$,
  ]).pipe(
    map(([micState, micLoading, isMicOpen, canOpenMic, isCallConnected]) => {
      const isJoinCallPanelActive = !isCallConnected;
      if (!isJoinCallPanelActive && !canOpenMic) {
        return {
          text: this.translateService.instant('The host has disabled this control'),
        };
      }
      if (micLoading) {
        if (this.providerStateService.callReconnectingValue) {
          return {
            text: this.translateService.instant(
              'Your network is unstable.\nReconnecting you to the call.',
            ),
          };
        } else {
          return {
            text: this.translateService.instant(
              'Setting-up your microphone. Please wait a moment.',
            ),
          };
        }
      }
      if (micState.hasError) {
        return {
          text: this.devicesManagerService.micErrorsMappings?.get(micState.errorType!),
          error: true,
        };
      }
      if (isMicOpen) {
        return {
          text: this.translateService.instant('Mute'),
        };
      } else {
        return {
          text: this.translateService.instant('Unmute'),
        };
      }
    }),
  );

  tooltipSigns$ = combineLatest([this.micTooltip$, this.disabled$]).pipe(
    map(([micTooltip, disabled]) => {
      if (!micTooltip.error && !disabled && micTooltip.text) {
        return [this.keyboardShortcutsCheckService.getAltKeySign(), 'M'];
      }
      return [];
    }),
  );

  tooltipDisabled$ = this.pictureInPictureService.isPIPActive$.pipe(
    map((isPIPActive) => isPIPActive),
  );

  // ========== Device Menu State ==========
  // State for the mic/speaker device selection menu

  deviceMenuMicHasError$ = this.localTracksManager.micState$.pipe(
    map((micState) => micState.hasError),
  );

  private _deviceMicLabel$ = this.localTracksManager.micState$.pipe(
    map((micState) =>
      this.truncateStringPipe.transform(micState.label ?? 'Default microphone', 25),
    ),
  );

  deviceMenuMicLabel$ = combineLatest([
    this._deviceMicLabel$,
    this.localTracksManager.micState$,
  ]).pipe(map(([micName, micState]) => this.getDeviceMenuMicLabel(micState, micName)));

  deviceMenuMicIcon$ = this.localTracksManager.micState$.pipe(
    map((micState) => (micState.hasError ? 'mic_off_with_error_white' : 'mic_icon')),
  );

  deviceMenuMicSubtitleError$ = this.localTracksManager.micState$.pipe(
    map((micState) => {
      const error = micState.errorType;
      if (
        error &&
        [
          DeviceErrorType.NO_INPUT_DETECTED,
          DeviceErrorType.DEFAULT_ERROR,
          DeviceErrorType.GET_USER_MEDIA_TIMEOUT,
        ].includes(error)
      ) {
        return this.translateService.instant('No input from device');
      }
      return undefined;
    }),
  );

  deviceMenuSpeakerHasPermissionError$ = this.localTracksManager.micState$.pipe(
    map((micState) => deviceHasPermissionError(micState)),
  );

  private deviceMenuCurrentSpeakerName$ = this.localTracksManager.speakerLabel$.pipe(
    map((speakerLabel) => this.truncateStringPipe.transform(speakerLabel ?? 'Default speaker', 25)),
  );

  deviceMenuSpeakerLabel$ = combineLatest([
    this.deviceMenuCurrentSpeakerName$,
    this.deviceMenuSpeakerHasPermissionError$,
  ]).pipe(
    map(([speakerName, hasPermissionError]) =>
      hasPermissionError ? this.translateService.instant('Permission required') : speakerName,
    ),
  );

  deviceMenuSpeakerIcon$ = this.deviceMenuSpeakerHasPermissionError$.pipe(
    map((hasPermissionError) =>
      hasPermissionError ? 'speaker_off_with_error_white' : 'volume_up_white',
    ),
  );

  private getDeviceMenuMicLabel(micState: Readonly<DeviceState>, micName: string): string {
    if (!micState.hasError) {
      return micName;
    }
    switch (micState.errorType) {
      case DeviceErrorType.PERMISSION_DENIED:
      case DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM:
        return this.translateService.instant('Permission required');
      case DeviceErrorType.NOT_FOUND:
        return this.translateService.instant('No mic available');
      default:
        return micName;
    }
  }

  openDeviceSelection(): void {
    this.devicesManagerService.openDeviceSelection();
  }

  async toggleMic(): Promise<void> {
    // Prevent toggling when disabled (includes loading state or permission denied)
    if (this._disabled$.value) {
      return;
    }
    if (!this.providerStateService.callConnectedValue) {
      const wasOff = !this.localTracksManager.getMicDeviceState().state;
      try {
        await this.localTracksManager.toggleJoinPreviewMic();
      } catch {
        // Device errors are surfaced via joinPreviewMicUnmuteFailed$ when unmute fails.
      }
      if (wasOff && this.localTracksManager.getMicDeviceState().hasError) {
        this._joinPreviewMicUnmuteFailed$.next();
      }
      return;
    }
    await this.localTracksManager.toggleDeviceState(DeviceType.AUDIO, true);
  }
}
